import { useMutation } from "@tanstack/react-query";
import {
  Box,
  CheckIcon,
  GKButton,
  GKTextField,
  Grid2,
  RightIcon,
  Typography,
} from "components";
import { useFetchQuery } from "hooks/useQueries";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useAppSelector } from "store/store";
import axiosInstance from "utils/axiosInstance";
import { ThemeContainer } from "utils/Theme";
import useDocumentTitle from "utils/useDocumentTitle";
import TelegramSetupModal from "./TelegramSetupModal";

type TelegramFormValues = {
  name: string;
  botToken: string;
  telegramId: string;
  firstName: string;
  botLink: string;
};

const TelegramSetup = () => {
  useDocumentTitle("Telegram Setup");

  const { theme } = ThemeContainer();

  const [openModal, setOpenModal] = useState(false);

  const userData = useAppSelector((state) => state?.user?.userData);

  const form = useForm<TelegramFormValues>({
    defaultValues: {
      name: "",
      botToken: "",
      telegramId: "",
      firstName: "",
      botLink: "",
    },
  });

  useFetchQuery({
    key: ["TELEGRAM_DETAILS"],
    route: "/telegram-details/",
    success: (res: any) => {
      form.reset({ ...res?.data?.data });
    },
  });

  const { mutate, isLoading } = useMutation({
    mutationFn: (requestedData: TelegramFormValues) =>
      axiosInstance.patch(
        `telegram-details/${userData.companyId}/`,
        requestedData,
      ),
    onSuccess: (response) => {
      toast.success(response.data?.message || "Action completed successfully.");
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message ||
          "Something went wrong, please try again.",
      );
    },
  });

  return (
    <>
      <form
        onSubmit={form.handleSubmit((values) => {
          mutate(values);
        })}
      >
        <Grid2 container spacing={2}>
          <Grid2 size={3}>
            <Typography
              fontSize={16}
              fontWeight={500}
              color={theme.palette.secondaryText.main}
            >
              Setup Telegram
            </Typography>
          </Grid2>
          <Grid2
            container
            size={9}
            spacing={2}
            display={"flex"}
            alignItems={"center"}
          >
            <Grid2 size={6}>
              <Controller
                control={form.control}
                name="name"
                rules={{
                  required: {
                    value: true,
                    message: "Please enter name",
                  },
                }}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <GKTextField
                      {...field}
                      requiredField
                      inputLabel={"Bot Name"}
                      error={!!error}
                      helperText={!!error && error.message}
                    />
                  );
                }}
              />
            </Grid2>
            <Grid2 size={6}>
              <Controller
                control={form.control}
                name="firstName"
                rules={{
                  required: {
                    value: true,
                    message: "Please enter firstName",
                  },
                }}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <GKTextField
                      {...field}
                      requiredField
                      inputLabel={"User Name"}
                      error={!!error}
                      helperText={!!error && error.message}
                    />
                  );
                }}
              />
            </Grid2>
            <Grid2 size={6}>
              <Controller
                control={form.control}
                name="botToken"
                rules={{
                  required: {
                    value: true,
                    message: "Please enter botToken",
                  },
                }}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <GKTextField
                      {...field}
                      requiredField
                      inputLabel={"Bot Token"}
                      error={!!error}
                      helperText={!!error && error.message}
                    />
                  );
                }}
              />
            </Grid2>
            <Grid2 size={6}>
              <Controller
                control={form.control}
                name="telegramId"
                rules={{
                  required: {
                    value: true,
                    message: "Please enter telegramId",
                  },
                }}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <GKTextField
                      {...field}
                      requiredField
                      inputLabel={"Bot Token No."}
                      error={!!error}
                      helperText={!!error && error.message}
                    />
                  );
                }}
              />
            </Grid2>
            <Grid2 size={6}>
              <Controller
                control={form.control}
                name="botLink"
                rules={{
                  required: {
                    value: true,
                    message: "Please enter Bot Link",
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <GKTextField
                    {...field}
                    requiredField
                    inputLabel="Bot Link"
                    error={!!error}
                    helperText={!!error && error.message}
                    slotProps={{
                      input: {
                        endAdornment: (
                          // <IconButton
                          //   onClick={() => {
                          //     if (field.value) {
                          //       navigator.clipboard.writeText(field.value);
                          //       toast.success("Copied to clipboard!");
                          //     } else {
                          //       toast.error("No link to copy!");
                          //     }
                          //   }}
                          //   style={{
                          //     padding: 0,
                          //   }}
                          // >
                          //   <CopyIcon />
                          // </IconButton>
                          <Box
                            display="inline-flex"
                            alignItems="center"
                            sx={{
                              cursor: field.value ? "pointer" : "not-allowed",
                              opacity: field.value ? 1 : 0.5,
                              userSelect: "none",
                            }}
                            onClick={() => {
                              if (!field.value) return;
                              navigator.clipboard.writeText(field.value);
                              toast.success("Copied to clipboard!");
                            }}
                          >
                            <Typography
                              fontSize={13}
                              fontWeight={500}
                              color="var(--grey-500)"
                              whiteSpace="nowrap"
                            >
                              Copy Link
                            </Typography>
                          </Box>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid2>
            <Grid2 size={6}>
              <GKButton
                loading={isLoading}
                variant="contained"
                type="submit"
                startIcon={<CheckIcon />}
              >
                Integrate
              </GKButton>
            </Grid2>
            <Grid2
              size={4.3}
              sx={{
                p: "12px",
                display: "flex",
                borderRadius: "8px",
                alignItems: "flex-start",
                background: "var(--neutral-50)",
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                textAlign="center"
              >
                <Typography
                  fontSize={12}
                  fontWeight={500}
                  color="var(--neutral-500)"
                >
                  Guide to setup Telegram Bot?
                </Typography>
                <Box
                  display="flex"
                  alignItems="center"
                  sx={{
                    cursor: "pointer",
                    color: "var(--primary-900)",
                    "&:hover": { textDecoration: "underline" },
                  }}
                  onClick={() => {
                    setOpenModal(true);
                  }}
                >
                  <Typography
                    fontSize={12}
                    fontWeight={500}
                    color="var(--primary-900)"
                    lineHeight={"150%"}
                  >
                    Click here
                  </Typography>
                  <RightIcon width={16} height={16} />
                </Box>
              </Box>
            </Grid2>
          </Grid2>
        </Grid2>
      </form>

      {openModal && (
        <TelegramSetupModal open={openModal} setOpen={setOpenModal} />
      )}
    </>
  );
};

export default TelegramSetup;
